import React, { Component } from "react";
import OverlayLoadingIndicator from "@common/OverlayLoadingIndicator";
import { APP_ROUTES, errorMessages } from "@utils/appConstants";
import { Alert, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { actions as signInActions } from "../../redux/actions/signIn";
import { actions as signUpActions } from "../../redux/actions/signUp";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { actions as authActions } from "../../app/auth";
import i18next from "i18next";
import { ActionCreators } from "../../redux/actions";
import Button from "@mui/material/Button";
import { withStyles } from "@mui/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Container, Box, Card } from "@mui/material";
import TextField from "@mui/material/TextField";
import { Email } from "@mui/icons-material";
import InputAdornment from "@mui/material/InputAdornment";
import { enqueueSnackbar } from "notistack";
import { eventTrackingCode } from "@utils/trackingCodes";
import * as msal from "@azure/msal-browser";
import { pxToRem } from "@utils/getFontValue";
import { WindowsIcon } from "../../theme/overrides/CustomIcons";
import { isValidEmail } from "@utils/utils";
import vrsLogo from "@/assets/images/vrsLogo.svg";

const styles = (theme) => ({
  root: {
    background: theme.palette.newBackgroundColor.paper,
    display: "flex",
    justifyContent: "center",
  },
  section: {
    width: "100%",
    maxWidth: 400,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-end",
    margin: theme.spacing(2, 0, 2, 2),
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  content: {
    maxWidth: 580,
    margin: "auto",
    display: "flex",
    minHeight: "100vh",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(12, 0),
  },
  card: {
    margin: theme.spacing(1),
    borderRadius: 16,
    boxShadow: theme.customShadows.card,
    background: theme.palette.background.paper,
    border: `0.5px solid ${theme.palette.background.neutral}`,
    padding: "24px",
  },
  mobileLogo: {
    marginTop: 22,
    marginBottom: 32,
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  button: {
    padding: "10px 20px",
    textTransform: "none",
  },
  buttonSubmit: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    boxShadow: theme.customShadows.primary,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    marginTop: "20px",
    marginBottom: "20px",
  },
  buttonCancel: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[300],
    boxShadow: theme.customShadows.hourCard,
    "&:hover": {
      backgroundColor: theme.palette.grey[400],
    },
  },
  signupLink: {
    fontSize: pxToRem(16),
  },
  forgotLink: {
    fontSize: pxToRem(13),
  },
  inputError: {
    width: "100%",
    marginTop: "0.25rem",
    fontSize: "80%",
    color: theme.palette.error.main,
    position: "relative",
    top: "-16px",
    display: "inline-flex",
  },
});

class LoginContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      validationErrors: {},
      isLoading: false,
      checkError: false,
      showPass: false,
    };
  }

  componentDidMount = () => {
    eventTrackingCode("login_page");

    if (this.props.location.pathname.startsWith("/confirmed")) {
      enqueueSnackbar(`${i18next.t("User confirmed successfully")}.`, {
        variant: "success",
      });
    }
    if (
      this.props.profileReducer &&
      this.props.profileReducer.resetPassSuccess &&
      this.props.profileReducer.resetPassSuccess.message
    ) {
      enqueueSnackbar(this.props.profileReducer.resetPassSuccess.message, {
        variant: "success",
      });
      this.props.actions.resetStore();
    }

    // check if user already logged-in, if yes, redirect to home
    if (authActions.isLoggedIn()) {
      this.props.history.push("/");
    }
  };

  componentDidUpdate = (prevProps) => {
    const { signIn, onToggleAuth } = this.props;

    if (signIn.data.access_token) {
      authActions.saveSession(signIn.data);
      this.setState({ isLoading: false });
      onToggleAuth();

      this.props.history.push("/");
    }

    if (prevProps.signIn !== this.props.signIn && this.state.checkError) {
      if (this.props.signIn.error) {
        this.setState({
          isLoading: false,
          checkError: false,
          errorMsg: this.props.signIn.error,
        });
      }
    }
  };

  validateForm = (callback) => {
    let { email, password } = this.state;
    const errors = {};
    if (!email) {
      errors.email = errorMessages.required;
    } else if (!isValidEmail(email)) {
      errors.email = errorMessages.invalidEmail;
    }
    if (!password) {
      errors.password = errorMessages.required;
    }

    if (Object.keys(errors).length === 0) {
      this.setState({ validationErrors: errors });
      callback();
    } else {
      this.setState({ validationErrors: errors });
    }
  };

  login = () => {
    let { email, password } = this.state;
    this.validateForm(async () => {
      this.setState({ isLoading: true, checkError: true });
      await this.props.signInActions.postLogin({ email, password }, false);
    });
  };

  handleAzureClick = async () => {
    const msalConfig = {
      auth: {
        clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID,
      },
    };
    const request = {
      scopes: ["profile", "user.read"],
    };

    try {
      const msalInstance = new msal.PublicClientApplication(msalConfig);
      const tokenResponse = await msalInstance.acquireTokenPopup(request);
      if (tokenResponse && tokenResponse.accessToken) {
        const data = {
          email: tokenResponse.account.username,
          microsoft_id: tokenResponse.uniqueId,
          microsoft_token: tokenResponse.accessToken,
        };
        this.setState({ isLoading: true, checkError: true });
        await this.props.signInActions.postLogin(data, true);
        authActions.setAzureAccountId(tokenResponse.account.homeAccountId);
      } else {
        this.setState({
          errorMsg: i18next.t("Azure AD authentication failed"),
        });
      }
    } catch (err) {
      this.setState({
        errorMsg: i18next.t("Azure AD authentication failed"),
      });
    }
  };

  render() {
    const { classes } = this.props;

    const { isValid, validationErrors, isLoading } = this.state;
    let emailError = null,
      passwordError = null;
    if (!isValid) {
      if (validationErrors.email) {
        emailError = (
          <div className={classes.inputError}>{validationErrors.email}</div>
        );
      }
      if (validationErrors.password) {
        passwordError = (
          <div className={classes.inputError}>{validationErrors.password}</div>
        );
      }
    }

    return (
      <>
        <Box className={classes.root}>
          <Container style={{ display: "flex" }}>
            <Box className={classes.section}>
              <Typography component="div" sx={{ mt: 5, mb: 5 }}>
                <Link href="https://www.visualregistration.no/">
                  <img
                    src={vrsLogo}
                    alt="Visual Registration Logo"
                    height={40}
                  />
                </Link>
              </Typography>
              <img alt="login illustration" src="/login-illustration.svg" />
            </Box>
            <Box component="div" className={classes.content}>
              <Box className={classes.mobileLogo}>
                <Link href="https://www.visualregistration.no/">
                  <img
                    src={vrsLogo}
                    alt="Visual Registration Logo"
                    height={52}
                  />
                </Link>
              </Box>
              <Card className={classes.card}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "24px",
                  }}
                >
                  <Typography variant="h4">{i18next.t("Sign in")}</Typography>
                  <Link
                    component={RouterLink}
                    variant="subtitle2"
                    to={APP_ROUTES.signUp}
                    className={classes.signupLink}
                  >
                    {i18next.t("Sign up")}
                  </Link>
                </Box>

                {this.state.errorMsg ? (
                  <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {this.state.errorMsg}
                  </Alert>
                ) : null}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Box>
                    <TextField
                      error={emailError}
                      autoFocus
                      fullWidth
                      label={i18next.t("Email")}
                      variant="outlined"
                      style={{ marginBottom: 15 }}
                      type="email"
                      placeholder={i18next.t("Email")}
                      required={true}
                      onChange={(e) => {
                        this.setState({ email: e.target.value });
                      }}
                      id="email"
                      name="email"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Email fontSize="small" style={{ color: "#ddd" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {emailError}
                    <TextField
                      error={passwordError}
                      fullWidth
                      autoComplete="new-password"
                      label={i18next.t("Password")}
                      style={{ marginBottom: 15 }}
                      variant="outlined"
                      type={this.state.showPass ? "text" : "password"}
                      data-sentry-ignore
                      placeholder={i18next.t("Password")}
                      onChange={(e) => {
                        this.setState({ password: e.target.value });
                      }}
                      id="login-password"
                      name="login_password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              this.setState({ showPass: !this.state.showPass })
                            }
                          >
                            {!this.state.showPass ? (
                              <VisibilityOffIcon
                                fontSize="small"
                                sx={{ color: "#ddd" }}
                              />
                            ) : (
                              <VisibilityIcon
                                fontSize="small"
                                sx={{ color: "#ddd" }}
                              />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                    {passwordError}
                    <Box
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Link
                        component={RouterLink}
                        variant="subtitle2"
                        to={APP_ROUTES.forgotPassword}
                        className={classes.forgotLink}
                      >
                        {`${i18next.t("Forgot password")}?`}
                      </Link>
                    </Box>
                    <Box>
                      <Button
                        variant="contained"
                        className={`${classes.button} ${classes.buttonSubmit}`}
                        type="submit"
                        onClick={this.login}
                        fullWidth
                      >
                        {i18next.t("Sign in")}
                      </Button>
                      <Button
                        variant="contained"
                        className={`${classes.button} ${classes.buttonCancel}`}
                        onClick={this.handleAzureClick}
                        startIcon={<WindowsIcon />}
                        fullWidth
                      >
                        {i18next.t("Sign in with Azure AD")}
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Card>
            </Box>
          </Container>
        </Box>
        <OverlayLoadingIndicator isLoading={isLoading} />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    signIn: state.signIn,
    profileReducer: state.profile,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(ActionCreators, dispatch),
    signInActions: bindActionCreators(signInActions, dispatch),
    signUpActions: bindActionCreators(signUpActions, dispatch),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(LoginContainer));
